import React, { useEffect, useState, useCallback, useRef } from 'react'
import { useParams } from 'react-router-dom'
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import GetAppIcon from '@mui/icons-material/GetApp'
import RefreshIcon from '@mui/icons-material/Refresh'
import PublicAppHeader from 'src/consumer/components/PublicAppHeader'
import { SEOComponent } from 'src/consumer/helpers/utilFunctions'
import { APP_CONSTANTS } from 'src/consumer/constants'
import { loadReceiptPdf } from 'src/consumer/services/receiptLinksService'
import { toast } from 'react-toastify'

/**
 * User-facing copy for receipt viewer errors (docs/apis/day-calendar/receipt-viewer.md §5.3).
 */
function getReceiptErrorMessage(status) {
  if (status === 410) {
    return 'This receipt link has expired. Please request a new one.'
  }
  if (status === 409) {
    return 'This link has reached its open limit. Please request a new one.'
  }
  if (status === 404) {
    return "We couldn't find this receipt."
  }
  return 'Something went wrong. Please try again.'
}

/**
 * Default filename for receipt PDF download (spec: Content-Disposition may provide receipt-{receiptId}.pdf).
 */
const DEFAULT_RECEIPT_FILENAME = 'receipt.pdf'

export default function ReceiptViewerPage() {
  const { token } = useParams()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const pdfContainerRef = useRef(null)

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null)

  const fetchPdf = useCallback(async () => {
    if (!token) {
      setError('Invalid link.')
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    setPdfBlobUrl((prev) => {
      if (prev && typeof URL !== 'undefined' && typeof URL.revokeObjectURL === 'function') {
        URL.revokeObjectURL(prev)
      }
      return null
    })
    try {
      const blob = await loadReceiptPdf(token)
      const url = URL.createObjectURL(blob)
      setPdfBlobUrl(url)
      if (isMobile && pdfContainerRef.current) {
        pdfContainerRef.current.scrollIntoView({ behavior: 'smooth' })
      }
    } catch (res) {
      const status = res?.status ?? (res?.response?.status ?? null)
      setError(getReceiptErrorMessage(status))
    } finally {
      setLoading(false)
    }
  }, [token, isMobile])

  useEffect(() => {
    fetchPdf()
  }, [fetchPdf])

  useEffect(() => {
    return () => {
      if (pdfBlobUrl && typeof URL !== 'undefined' && typeof URL.revokeObjectURL === 'function') {
        URL.revokeObjectURL(pdfBlobUrl)
      }
    }
  }, [pdfBlobUrl])

  const handleDownload = () => {
    if (!pdfBlobUrl) return
    const a = document.createElement('a')
    a.href = pdfBlobUrl
    a.download = DEFAULT_RECEIPT_FILENAME
    a.rel = 'noopener noreferrer'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    toast.success('Download started.')
  }

  if (loading) {
    return (
      <>
        <PublicAppHeader />
        <SEOComponent
          title="View receipt | AUMHUM"
          description="View your receipt"
          image={APP_CONSTANTS.APP_LOGO_LINK}
        />
        <Box
          sx={{
            minHeight: '70vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: 3,
            background: 'linear-gradient(180deg, rgba(248,250,252,0.6) 0%, transparent 100%)',
          }}
        >
          <CircularProgress size={40} thickness={4} sx={{ color: 'primary.main', mb: 2 }} />
          <Typography variant="body2" color="text.secondary">
            Loading receipt…
          </Typography>
        </Box>
      </>
    )
  }

  if (error && !pdfBlobUrl) {
    return (
      <>
        <PublicAppHeader />
        <SEOComponent
          title="Receipt link | AUMHUM"
          description="View your receipt"
          image={APP_CONSTANTS.APP_LOGO_LINK}
        />
        <Box
          sx={{
            minHeight: '60vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 3,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              maxWidth: 420,
              p: 4,
              textAlign: 'center',
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                bgcolor: 'error.50',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              <ErrorOutlineIcon sx={{ fontSize: 32, color: 'error.main' }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              Unable to load receipt
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
              {error}
            </Typography>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={fetchPdf}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
            >
              Retry
            </Button>
          </Paper>
        </Box>
      </>
    )
  }

  const pdfMinHeight = isMobile ? 420 : 400
  return (
    <>
      <PublicAppHeader />
      <SEOComponent
        title="View receipt | AUMHUM"
        description="View your receipt"
        image={APP_CONSTANTS.APP_LOGO_LINK}
      />
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'grey.50',
          pb: { xs: 4, md: 0 },
          ...(isMobile ? {} : { height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column' }),
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 1200,
            mx: 'auto',
            px: { xs: 2, sm: 3, lg: 4 },
            pt: { xs: 2, sm: 3 },
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              mb: { xs: 2, md: 2 },
              fontSize: { xs: '1.25rem', sm: '1.5rem' },
              flexShrink: 0,
            }}
          >
            Your receipt
          </Typography>
          <Box
            ref={pdfContainerRef}
            sx={{
              width: '100%',
              flex: 1,
              minHeight: pdfMinHeight,
              display: 'flex',
              flexDirection: 'column',
              minWidth: 0,
            }}
          >
            <Paper
              elevation={0}
              component="div"
              sx={{
                position: 'relative',
                width: '100%',
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                minHeight: pdfMinHeight,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box
                sx={{
                  flexShrink: 0,
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  px: 1.5,
                  py: 1,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                }}
              >
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<GetAppIcon />}
                  onClick={handleDownload}
                  sx={{ textTransform: 'none', borderRadius: 2 }}
                >
                  Download
                </Button>
              </Box>
              <Box
                sx={{
                  position: 'relative',
                  flex: 1,
                  minHeight: 0,
                }}
              >
                <iframe
                  title="Receipt PDF"
                  src={pdfBlobUrl}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    border: 'none',
                  }}
                />
              </Box>
            </Paper>
          </Box>
        </Box>
      </Box>
    </>
  )
}
